import React, { createContext, useContext, useState, ReactNode, useCallback } from 'react';

interface ShiftCalendarContextValue {
  // Calendar state
  expandedDayKeys: string[];
  setExpandedDayKeys: (keys: string[]) => void;
  
  // Loading states
  loading: boolean;
  setLoading: (loading: boolean) => void;
  
  // Error handling
  error: string | null;
  setError: (error: string | null) => void;
  
  // Utility functions
  toggleDayExpansion: (dayKey: string) => void;
}

const ShiftCalendarContext = createContext<ShiftCalendarContextValue | undefined>(undefined);

interface ShiftCalendarProviderProps {
  children: ReactNode;
  initialExpandedDays?: string[];
}

export const ShiftCalendarProvider: React.FC<ShiftCalendarProviderProps> = ({
  children,
  initialExpandedDays = []
}) => {
  const [expandedDayKeys, setExpandedDayKeys] = useState<string[]>(initialExpandedDays);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggleDayExpansion = useCallback((dayKey: string) => {
    setExpandedDayKeys(prev => {
      const isExpanded = prev.includes(dayKey);
      if (isExpanded) {
        return prev.filter(key => key !== dayKey);
      } else {
        return [...prev, dayKey];
      }
    });
  }, []);

  const value: ShiftCalendarContextValue = {
    expandedDayKeys,
    setExpandedDayKeys,
    loading,
    setLoading,
    error,
    setError,
    toggleDayExpansion
  };

  return (
    <ShiftCalendarContext.Provider value={value}>
      {children}
    </ShiftCalendarContext.Provider>
  );
};

export const useShiftCalendar = () => {
  const context = useContext(ShiftCalendarContext);
  if (context === undefined) {
    throw new Error('useShiftCalendar must be used within a ShiftCalendarProvider');
  }
  return context;
};